import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faPlug, faSpinner } from '@fortawesome/free-solid-svg-icons';
import WebflowPublishNotice from './WebflowPublishNotice';
import WebflowDisconnectedNotice from './WebflowDisconnectedNotice';
import './WebflowSitePanel.css';

/**
 * The Webflow channel: one row for the agent's site with a link to it, what a
 * visitor gets there, and a switch. The backend's `state` (webflow/status)
 * tells the truth about the published page:
 *   live         on, and the published page carries the script
 *   pending      on, the page gets it with the next publish
 *   off_in_page  off: no longer answering; the script leaves with the next publish
 *   off          not on the site
 * Switching never publishes (Webflow: the customer publishes, because a
 * publish pushes every staged change); a publish line appears only when a
 * publish changes something.
 */
export default function WebflowSitePanel({
  botId, status, state, site, sites, selectedSiteId, onSelectSite,
  busy, error, disconnectedSites, onConnect, onDisconnect, onToggle, onPublished, onRetry,
}) {
  const { t } = useTranslation();
  const [choosing, setChoosing] = useState(false);
  const connected = status === 'token_no_code' || status === 'token_with_code';
  const on = status === 'token_with_code';
  // The site picker shows until the agent was on a site, or when the owner
  // asks to change it while the agent is off.
  const picking = status === 'token_no_code' && (!site || choosing);
  const shown = picking ? (sites || []).find((s) => s.id === selectedSiteId) : site;
  const host = (shown?.url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

  const pill = on
    ? (state === 'pending' ? ['pending', t('wf_state_pending')] : ['live', t('wf_state_live')])
    : ['off', t('wf_state_off')];

  const toggle = () => {
    setChoosing(false);
    onToggle(!on);
  };

  return (
    <div className="wf-panel">
      {connected && (
        <div className="wf-panel__head">
          <span className="wf-panel__conn"><span className="wf-panel__dot" />{t('wf_panel_connected')}</span>
          <span className="wf-panel__spacer" />
          <button type="button" className="wf-panel__link" onClick={onDisconnect} disabled={busy}>
            {t('wf_panel_disconnect')}
          </button>
        </div>
      )}

      {error && <p className="wf-panel__error" role="alert">{error}</p>}

      {status === null || status === undefined ? (
        <div className="wf-panel__loading"><FontAwesomeIcon icon={faSpinner} spin /></div>
      ) : status === 'no_token' ? (
        <>
          {disconnectedSites && <WebflowDisconnectedNotice sites={disconnectedSites} />}
          <p className="wf-panel__intro">{t('integration_webflow_connect_text')}</p>
          <button type="button" className="wf-panel__connect" onClick={onConnect} disabled={busy}>
            <FontAwesomeIcon icon={faPlug} /> {t('connect')}
          </button>
        </>
      ) : connected ? (
        <>
          <p className="wf-panel__intro">{t('wf_panel_intro')}</p>
          <div className="wf-site">
            <div className="wf-site__main">
              {picking ? (
                <select
                  className="wf-site__select"
                  aria-label={t('wf_panel_choose_site')}
                  value={selectedSiteId || ''}
                  onChange={(e) => onSelectSite(e.target.value)}
                  disabled={busy}
                >
                  {(sites || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              ) : (
                <div className="wf-site__name">{shown?.name}</div>
              )}
              {host && (
                <a className="wf-site__url" href={`https://${host}`} target="_blank" rel="noopener noreferrer">
                  {host} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              )}
            </div>
            <span className={`wf-pill wf-pill--${pill[0]}`} data-testid="wf-state">{pill[1]}</span>
            {busy ? <FontAwesomeIcon icon={faSpinner} spin className="wf-site__busy" /> : null}
            <Switch
              checked={on}
              onChange={toggle}
              disabled={busy || (picking && !selectedSiteId)}
              inputProps={{ 'aria-label': t('wf_panel_toggle') }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
              }}
            />
          </div>
          {!busy && (state === 'pending' || state === 'off_in_page') && shown && (
            <WebflowPublishNotice
              key={`${state}-${shown.id}`}
              inline
              botId={botId}
              siteId={shown.id}
              siteName={shown.name}
              text={state === 'pending' ? t('wf_state_pending_text') : t('wf_state_off_in_page_text')}
              onPublished={onPublished}
            />
          )}
          {!on && site && !choosing && (sites || []).length > 1 && (
            <button type="button" className="wf-panel__link wf-panel__change" onClick={() => setChoosing(true)} disabled={busy}>
              {t('wf_panel_change_site')}
            </button>
          )}
        </>
      ) : (
        <p className="wf-panel__error">
          {t('integration_unexpected_state')}{' '}
          <button type="button" className="wf-panel__link" onClick={onRetry}>{t('wf_retry')}</button>
        </p>
      )}

      {connected && (
        <details className="wf-panel__help" data-testid="wf-removal-help">
          <summary>{t('wf_panel_removal_title')}</summary>
          <p>{t('wf_removal_help')}</p>
        </details>
      )}
    </div>
  );
}
